import { useState } from 'react';
import { Block } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Chip } from '@jotpsych/ui';
import { useSnackbar } from 'notistack';

import { useUserRoles } from '@features/user';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { useGetMcpTokensQuery, useRevokeMcpTokenMutation } from '../store/hooks';
import type { McpUserTokenInfo } from '../store/hooks';
import { formatDateTime } from '../utils/formatting';

/**
 * Admin-only registry of every minted MCP token, with revocation.
 * Moderators cannot see this page (the backend 403s the underlying
 * endpoints as well).
 */
export default function McpTokensPage() {
  const { enqueueSnackbar } = useSnackbar();
  const roles = useUserRoles();
  const isAdmin = roles.includes('Admin');

  const { data, isLoading, error } = useGetMcpTokensQuery(undefined, {
    skip: !isAdmin,
  });
  const [revokeToken, { isLoading: isRevoking }] = useRevokeMcpTokenMutation();

  const [tokenToRevoke, setTokenToRevoke] = useState<McpUserTokenInfo | null>(null);

  const handleConfirmRevoke = async () => {
    if (!tokenToRevoke) return;
    try {
      await revokeToken({ mcpUserTokenId: tokenToRevoke.mcp_user_token_id }).unwrap();
      enqueueSnackbar('Token revoked', { variant: 'success' });
      setTokenToRevoke(null);
    } catch (err) {
      const e = err as { status?: number | string; data?: { message?: string } };
      const detail = e?.data?.message ?? (e?.status ? `HTTP ${e.status}` : null);
      enqueueSnackbar(
        detail ? `Failed to revoke token: ${detail}` : 'Failed to revoke token',
        { variant: 'error' },
      );
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert severity="error">MCP token administration is available to admins only.</Alert>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert severity="error">Unable to load MCP tokens.</Alert>
        </main>
      </div>
    );
  }

  const tokens = data?.tokens ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">MCP Tokens</h1>
          <p className="text-gray-600 mt-1">
            Every minted personal MCP token. Revoking a token immediately blocks its access to
            the admin MCP server; the owner can generate a new one from the MCP Access page.
          </p>
        </div>

        <DataTable<McpUserTokenInfo>
          title="Minted tokens"
          loading={isLoading}
          data={tokens}
          emptyMessage="No tokens have been minted yet"
          columns={[
            {
              key: 'user_id',
              label: 'User',
              render: value => (
                <span className="font-mono text-xs text-gray-700">{String(value)}</span>
              ),
            },
            {
              key: 'organization_id',
              label: 'Organization',
              render: value =>
                value ? (
                  <span className="font-mono text-xs text-gray-700">{String(value)}</span>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                ),
            },
            {
              key: 'token_prefix',
              label: 'Token',
              render: value => (
                <span className="font-mono text-xs text-gray-700">{String(value)}…</span>
              ),
            },
            {
              key: 'scope',
              label: 'Scope',
              render: value => (
                <Chip
                  label={String(value)}
                  variant="soft"
                  color={value === 'write' ? 'warning' : 'default'}
                  size="small"
                />
              ),
            },
            {
              key: 'revoked_at',
              label: 'Status',
              render: (_value, row) =>
                row.revoked_at ? (
                  <Tooltip title={`Revoked ${formatDateTime(row.revoked_at)} by ${row.revoked_by ?? 'unknown'}`}>
                    <span>
                      <Chip label="Revoked" variant="soft" color="error" size="small" />
                    </span>
                  </Tooltip>
                ) : (
                  <Chip label="Active" variant="soft" color="success" size="small" />
                ),
            },
            {
              key: 'created_at',
              label: 'Created',
              render: value => (value ? formatDateTime(String(value)) : 'N/A'),
            },
            {
              key: 'last_used_at',
              label: 'Last used',
              render: value => (value ? formatDateTime(String(value)) : 'Never'),
            },
            {
              key: 'mcp_user_token_id',
              label: '',
              render: (_value, row) =>
                row.revoked_at ? null : (
                  <Tooltip title="Revoke token">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={event => {
                        event.stopPropagation();
                        setTokenToRevoke(row);
                      }}
                    >
                      <Block fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
            },
          ]}
        />

        <Dialog
          open={tokenToRevoke !== null}
          onClose={() => !isRevoking && setTokenToRevoke(null)}
          maxWidth="xs"
        >
          <DialogTitle>Revoke MCP Token</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The owner will lose MCP access immediately.
            </Alert>
            <Typography>
              This will revoke <strong>{tokenToRevoke?.token_prefix}…</strong> belonging to{' '}
              <strong>{tokenToRevoke?.user_id}</strong>. They can mint a new token from the MCP
              Access page.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTokenToRevoke(null)} disabled={isRevoking}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRevoke}
              variant="contained"
              color="error"
              disabled={isRevoking}
              startIcon={isRevoking ? <CircularProgress size={16} /> : undefined}
            >
              {isRevoking ? 'Revoking...' : 'Revoke'}
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}
